// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { SelectField, SelectOption } from 'components/molecules/SelectField';
import CustomPagination from 'components/atoms/CustomPagination';
import { CustomCheckBox } from 'components/atoms/CustomCheckbox';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BigNumber from 'bignumber.js';
import styles from './ProcessingListContent.module.scss';
import { normalizeNumber } from 'util/commons';
import {
  useFetchProcessingMaterialsQuery,
  useResetInputResourcesMutation,
} from 'apis/compressions_user/compressionUserApi';
import { getMaterialNamesBySite } from 'apis/operator_site/siteApi';
import { selectUser } from 'redux/slices/userSlice';
import { ProcessingMode } from 'util/Enums';

const PAGE_SIZE = 20;

const ProcessingListContent: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const isAuto = user.processingMode === ProcessingMode.Auto;

  const [materialOptions, setMaterialOptions] = useState<SelectOption[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [materialFilter, setMaterialFilter] = useState<string>('');

  useEffect(() => {
    getMaterialNamesBySite({
      siteId: '',
      screen: 'materialsInProcessing',
      successCallback: (names) => {
        setMaterialOptions(names.map((name) => ({ label: name, value: name })));
        if (names.length > 0) {
          setMaterialFilter(names[0]);
        }
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, refetch } = useFetchProcessingMaterialsQuery({
    page: currentPage + 1,
    take: PAGE_SIZE,
    materialName: materialFilter || undefined,
  });

  const rows = data?.data ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  // Build dynamic column headers from current page data
  const typeNames = useMemo(() => {
    const names = new Set<string>();
    rows.forEach((row) =>
      row.materialTypes.forEach((mt) => names.add(mt.typeName))
    );
    return Array.from(names);
  }, [rows]);

  const allChecked =
    rows.length > 0 &&
    checkedIds.size === new Set(rows.map((r) => r.processingObjectId)).size;

  const handleToggleAll = () => {
    if (allChecked) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(rows.map((r) => r.processingObjectId)));
    }
  };

  const handleToggleRow = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const [resetInputResources] = useResetInputResourcesMutation();

  const handleReset = async () => {
    if (checkedIds.size > 0) {
      const result = await resetInputResources({
        processingObjectIds: Array.from(checkedIds),
      });
      setCheckedIds(new Set());
      setCurrentPage(0);
      refetch();
      // Error case: toast is already handled by the global error callback on request failure
      if (!('error' in result)) {
        toast.success(t('processing.reset_success'));
      }
    }
  };

  const handlePageChange = ({ selected }: { selected: number }) => {
    setCurrentPage(selected);
    setCheckedIds(new Set());
  };

  const totalColSpan = isAuto ? 3 + typeNames.length * 2 : 5;

  return (
    <div className={styles.listContent}>
      {/* Material filter */}
      <div className={styles.filterBar}>
        <Form
          key={materialFilter}
          initialValues={{ materialName: materialFilter }}
          onSubmit={() => {}}
        >
          {() => (
            <form>
              <SelectField
                name="materialName"
                options={[
                  { label: t('processing.filter_material'), value: '' },
                  ...materialOptions,
                ]}
                value={materialFilter}
                onChange={(v) => {
                  setMaterialFilter(String(v));
                  setCurrentPage(0);
                  setCheckedIds(new Set());
                }}
                isDarkmode
              />
            </form>
          )}
        </Form>
      </div>

      <div className={styles.tableWrapper}>
        <table
          className={`${styles.table}${
            !isAuto ? ` ${styles.tableManual}` : ''
          }`}
        >
          <thead>
            <tr>
              {/* Select-all checkbox */}
              <th
                className={styles.colCheckbox}
                rowSpan={isAuto ? 2 : 1}
                onClick={rows.length ? handleToggleAll : undefined}
                style={{
                  cursor: rows.length ? 'pointer' : 'default',
                  opacity: rows.length ? 1 : 0.4,
                }}
              >
                <div className={styles.checkboxHeader}>
                  <span className={styles.checkboxHeaderText}>
                    {allChecked
                      ? t('common.deselect_all')
                      : t('common.select_all')}
                  </span>
                </div>
              </th>
              <th className={styles.colResourceId} rowSpan={isAuto ? 2 : 1}>
                {t('processing.col_resource_id')}
              </th>
              <th className={styles.colMaterial} rowSpan={isAuto ? 2 : 1}>
                {t('processing.col_material_name')}
              </th>
              {isAuto ? (
                typeNames.map((name) => (
                  <th key={name} colSpan={2}>
                    {name}
                  </th>
                ))
              ) : (
                <>
                  <th className={styles.colSub}>
                    {t('processing.col_inputted')}
                  </th>
                  <th className={styles.colSub}>
                    {t('processing.col_remaining')}
                  </th>
                </>
              )}
            </tr>
            {isAuto && typeNames.length > 0 && (
              <tr>
                {typeNames.map((name) => (
                  <>
                    <th key={`${name}-inputted`} className={styles.colSub}>
                      {t('processing.col_inputted')}
                    </th>
                    <th key={`${name}-remaining`} className={styles.colSub}>
                      {t('processing.col_remaining')}
                    </th>
                  </>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row) => (
                <tr key={row.processingObjectId} className={styles.row}>
                  <td className={styles.colCheckbox}>
                    <CustomCheckBox
                      id={`row-check-${row.processingObjectId}`}
                      checked={checkedIds.has(row.processingObjectId)}
                      onChange={() => handleToggleRow(row.processingObjectId)}
                      darkMode
                    />
                  </td>
                  <td className={styles.colResourceId}>
                    {row.processingObjectId}
                  </td>
                  <td className={styles.colMaterial}>{row.materialName}</td>
                  {isAuto ? (
                    typeNames.map((name) => {
                      const mt = row.materialTypes.find(
                        (m) => m.typeName === name
                      );
                      return (
                        <>
                          <td
                            key={`${row.processingObjectId}-${name}-w`}
                            className={styles.colSub}
                          >
                            {mt
                              ? `${normalizeNumber({
                                  value: BigNumber(mt.weight)
                                    .minus(mt.remainingWeight)
                                    .toNumber(),
                                })}kg`
                              : '0kg'}
                          </td>
                          <td
                            key={`${row.processingObjectId}-${name}-r`}
                            className={styles.colSub}
                          >
                            {mt
                              ? `${normalizeNumber({
                                  value: parseFloat(mt.remainingWeight),
                                })}kg`
                              : '0kg'}
                          </td>
                        </>
                      );
                    })
                  ) : (
                    <>
                      <td className={styles.colSub}>
                        {row.materialTypes.length
                          ? `${normalizeNumber({
                              value: row.materialTypes.reduce(
                                (sum, m) =>
                                  BigNumber(sum)
                                    .plus(m.weight)
                                    .minus(m.remainingWeight)
                                    .toNumber(),
                                0
                              ),
                            })}kg`
                          : '0kg'}
                      </td>
                      <td className={styles.colSub}>
                        {row.materialTypes.length
                          ? `${normalizeNumber({
                              value: row.materialTypes.reduce(
                                (sum, m) =>
                                  BigNumber(sum)
                                    .plus(m.remainingWeight)
                                    .toNumber(),
                                0
                              ),
                            })}kg`
                          : '0kg'}
                      </td>
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={totalColSpan} className={styles.noData}>
                  {t('messages.M_008')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.bottomBar}>
        <div className={styles.paginationContainer}>
          <CustomPagination
            pageCount={totalPages}
            forcePage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
        <button
          className={styles.resetBtn}
          onClick={handleReset}
          disabled={!checkedIds.size}
        >
          {t('processing.btn_reset')}
        </button>
      </div>
    </div>
  );
};

export default ProcessingListContent;
